import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

/**
 * ProtectedRoute Component
 * Protects routes that require authentication
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredRoles = null,
  fallbackComponent = null,
  redirectTo = '/login'
}) => {
  const { user, loading, isAuthenticated, hasRole, hasAnyRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f1eee4'
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check role-based access if roles are specified
  if (requiredRole && !hasRole(requiredRole)) {
    return fallbackComponent || (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f1eee4',
          p: 3
        }}
      >
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
          You don't have the required permissions to access this page.
          <br />
          Required role: {requiredRole}
          <br />
          Your role: {user?.role || 'Unknown'}
        </Typography>
      </Box>
    );
  }

  // Check multiple roles if specified
  if (requiredRoles && !hasAnyRole(requiredRoles)) {
    return fallbackComponent || (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f1eee4',
          p: 3
        }}
      >
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
          You don't have the required permissions to access this page.
          <br />
          Required roles: {requiredRoles.join(', ')}
          <br />
          Your role: {user?.role || 'Unknown'}
        </Typography>
      </Box>
    );
  }

  // User is authenticated and has required permissions
  return children;
};

/**
 * AdminRoute Component
 * Shortcut for routes that require admin or super_admin role
 */
export const AdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute requiredRoles={['admin', 'super_admin']} {...props}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * SuperAdminRoute Component
 * Shortcut for routes that require super_admin role only
 */
export const SuperAdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute requiredRole="super_admin" {...props}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * RoleBasedComponent
 * Conditionally renders components based on user role
 */
export const RoleBasedComponent = ({ 
  allowedRoles, 
  children, 
  fallback = null 
}) => {
  const { hasAnyRole } = useAuth();

  if (!allowedRoles || hasAnyRole(allowedRoles)) {
    return children;
  }

  return fallback;
};

/**
 * AuthGuard Hook
 * Custom hook for programmatic authentication checks
 */
export const useAuthGuard = () => {
  const { isAuthenticated, user, hasRole, hasAnyRole } = useAuth();

  const requireAuth = (callback) => {
    if (isAuthenticated) {
      callback();
    } else {
      // Redirect to login or show error
      window.location.href = '/login';
    }
  };

  const requireRole = (role, callback, fallback) => {
    if (hasRole(role)) {
      callback();
    } else if (fallback) {
      fallback();
    }
  };

  const requireAnyRole = (roles, callback, fallback) => {
    if (hasAnyRole(roles)) {
      callback();
    } else if (fallback) {
      fallback();
    }
  };

  return {
    isAuthenticated,
    user,
    requireAuth,
    requireRole,
    requireAnyRole,
    hasRole,
    hasAnyRole
  };
};

export default ProtectedRoute;
